import { Check, Minus } from "lucide-react";
import { ReactNode } from "react";
import {
  Checkbox as AriaCheckbox,
  CheckboxGroup as AriaCheckboxGroup,
  CheckboxGroupProps as AriaCheckboxGroupProps,
  CheckboxProps as AriaCheckboxProps,
  CheckboxRenderProps,
  composeRenderProps,
  ValidationResult,
} from "react-aria-components";
import { composeTailwindRenderProps } from "../../utils";
import { Description, FieldError, Label } from "../Field";

/**
 * CheckboxGroup
 *
 * Groups multiple checkboxes with shared label/description.
 */
type SelectionVariant = "primary";
type LegacySelectionVariant = "default" | "brand";

const selectionFocusClass = "focus:ring-action-primary";
const selectionColorClasses =
  "bg-action-primary border-action-primary text-[color:var(--color-action-primary-text)] hover:bg-action-primary-hover hover:border-action-primary-hover";

export interface CheckboxGroupProps extends Omit<AriaCheckboxGroupProps, "children"> {
  label?: string;
  children?: ReactNode;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
}

/**
 * Checkbox
 *
 * Single checkbox with Edges visuals.
 */
export interface CheckboxProps extends Omit<AriaCheckboxProps, "children"> {
  children?: ReactNode | ((props: CheckboxRenderProps) => ReactNode);
  /**
   * Deprecated compatibility prop. Checkbox now has one selected treatment; `default` and `brand` both map to primary.
   * @default "primary"
   */
  variant?: SelectionVariant | LegacySelectionVariant;
}

export function CheckboxGroup(props: CheckboxGroupProps) {
  return (
    <AriaCheckboxGroup {...props} className={composeTailwindRenderProps(props.className, "flex flex-col gap-2")}>
      <Label>{props.label}</Label>
      {props.children}
      {props.description && <Description>{props.description}</Description>}
      <FieldError>{typeof props.errorMessage === "function" ? undefined : props.errorMessage}</FieldError>
    </AriaCheckboxGroup>
  );
}

function getCheckboxStyles({ isDisabled }: { isDisabled?: boolean }) {
  // `relative` contains react-aria's visually-hidden absolutely-positioned <input>. Without it the
  // input resolves against a distant positioned ancestor, escaping any scroll container in between —
  // Chrome then scroll-jumps those containers (even overflow-hidden ones) when the input gains focus.
  return `relative flex gap-2 items-center group text-sm transition ${isDisabled ? "text-text-disabled" : "text-text-secondary"}`;
}

function getBoxStyles({
  isSelected,
  isInvalid,
  isDisabled,
  isIndeterminate,
}: {
  isSelected?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  isIndeterminate?: boolean;
}) {
  const baseStyles =
    "w-5 h-5 flex-shrink-0 rounded-[var(--control-border-radius)] flex items-center justify-center border-2 transition ring-offset-2 focus:ring-2";

  const focusRingColor = selectionFocusClass;

  let colorStyles = "";
  if (isDisabled) {
    colorStyles = "border-border-muted bg-background-muted";
  } else if (isInvalid) {
    colorStyles = "border-feedback-error-border bg-feedback-error-background";
  } else if (isSelected || isIndeterminate) {
    colorStyles = selectionColorClasses;
  } else {
    colorStyles = "bg-background-input border-border-input hover:border-border-focus";
  }

  return `${baseStyles} ${focusRingColor} ${colorStyles}`;
}

const iconStyles = "w-4 h-4 text-current group-disabled:text-text-disabled";

export interface CheckboxIndicatorProps {
  isSelected?: boolean;
  isIndeterminate?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  className?: string;
}

/**
 * The checkbox box-and-tick visual with no input of its own. Exists so
 * composite controls that own their own selection semantics — `MultiSelect`'s
 * `role="option"` rows, where a real `<input>` would be invalid ARIA and would
 * steal focus from the combobox — still render the house checkbox.
 */
export function CheckboxIndicator({ className, ...states }: CheckboxIndicatorProps) {
  return (
    <div aria-hidden="true" className={`${getBoxStyles(states)} ${className || ""}`}>
      {states.isIndeterminate ? (
        <Minus aria-hidden className={iconStyles} />
      ) : states.isSelected ? (
        <Check aria-hidden className={iconStyles} />
      ) : null}
    </div>
  );
}

export function Checkbox(props: CheckboxProps) {
  const { children, variant: _variant, ...otherProps } = props;
  return (
    <AriaCheckbox
      {...otherProps}
      className={composeRenderProps(
        props.className,
        (className, renderProps) => `${getCheckboxStyles(renderProps)} ${className || ""}`
      )}
    >
      {(renderProps) => {
        const { isSelected, isIndeterminate, isDisabled, isInvalid } = renderProps;

        return (
          <>
            <CheckboxIndicator
              isSelected={isSelected}
              isIndeterminate={isIndeterminate}
              isDisabled={isDisabled}
              isInvalid={isInvalid}
            />
            {typeof children === "function" ? children(renderProps) : children}
          </>
        );
      }}
    </AriaCheckbox>
  );
}
